import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { useSectionTracking } from "./AnalyticsProvider";

export default function StorySection() {
  const sectionRef = useSectionTracking("story");
  const quoteRef = useRef(null);
  const storyRef = useRef(null);
  const quoteInView = useInView(quoteRef, { once: true, margin: "-60px" });
  const storyInView = useInView(storyRef, { once: true, margin: "-80px" });

  return (
    <section id="story" ref={sectionRef} className="relative overflow-hidden" data-testid="story-section">
      {/* THE QUOTE — its own full-width moment */}
      <div ref={quoteRef} className="py-10 sm:py-14 lg:py-18 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.p
            initial={{ opacity: 0 }}
            animate={quoteInView ? { opacity: 1 } : {}}
            transition={{ duration: 1.2 }}
            className="overline mb-8"
          >
            The Discovery
          </motion.p>
          <motion.blockquote
            initial={{ opacity: 0, y: 20 }}
            animate={quoteInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 1.5, delay: 0.3 }}
            className="font-serif text-4xl sm:text-5xl lg:text-6xl xl:text-7xl italic text-[#C5A059] leading-[1.15] tracking-tight"
          >
            One satisfies a desire. The other rewrites history.
          </motion.blockquote>
          <motion.p
            initial={{ opacity: 0 }}
            animate={quoteInView ? { opacity: 1 } : {}}
            transition={{ duration: 1, delay: 0.8 }}
            className="text-sm text-[#B8B3AA]/75 mt-8"
          >
            1,055 carats — Muzo, Colombia
          </motion.p>
        </div>
      </div>

      {/* THE STORY — image + narrative */}
      <div className="py-10 lg:py-14" ref={storyRef}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              animate={storyInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 1 }}
              className="lg:col-span-5"
            >
              <div className="relative">
                <img
                  src="/media/photos/gubelin_book_01.jpg"
                  alt="The Bridgette Emerald — Gübelin Gemological Profile"
                  className="w-full h-[50vh] sm:h-[60vh] lg:h-[70vh] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#111110]/40 to-transparent" />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={storyInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 1, delay: 0.3 }}
              className="lg:col-span-7 flex flex-col justify-center"
            >
              <p className="text-base lg:text-lg text-[#B8B3AA] leading-relaxed mb-6 max-w-xl font-light">
                Discovered in 2023 deep within Colombia's legendary Muzo mines, The Bridgette Emerald
                emerged as a 1,055-carat hexagonal prism of such extraordinary size and clarity that
                Gübelin Gem Lab — the world's most prestigious gemological authority — broke with
                150 years of tradition to create something that had never existed before.
              </p>

              <p className="text-base lg:text-lg text-[#B8B3AA] leading-relaxed mb-6 max-w-xl font-light">
                For the first time in their history, they produced a certified premium Gemological
                Profile Book for a raw, uncut emerald. A distinction no other collector, museum, or
                institution on Earth can claim. The next owner of Bridgette will possess the only raw emerald
                ever to receive it.
              </p>

              <p className="text-sm text-[#B8B3AA]/70 italic mb-8 max-w-xl leading-relaxed border-l border-[#C5A059]/20 pl-4">
                Gübelin's analysis confirmed the specimen is transparent to translucent with no evidence
                of clarity enhancement, exhibiting a saturated green colour with a slightly bluish secondary hue —
                the most coveted chromatic profile for Colombian emeralds.
                <span className="block text-xs text-[#B8B3AA]/75 mt-2 not-italic">Source: Gübelin Gemological Profile, Report No. 25070096</span>
              </p>

              <div className="flex items-center gap-6 pt-4 border-t border-[#C5A059]/15">
                <div>
                  <p className="text-xs text-[#C5A059]/60 uppercase tracking-wider">Certified By</p>
                  <p className="text-base text-[#F4F0EA]/70 mt-1">Gübelin Gem Lab, Zurich</p>
                </div>
                <div className="w-px h-10 bg-[#C5A059]/15" />
                <div>
                  <p className="text-xs text-[#C5A059]/60 uppercase tracking-wider">Origin</p>
                  <p className="text-base text-[#F4F0EA]/70 mt-1">Muzo, Colombia</p>
                </div>
                <div className="w-px h-10 bg-[#C5A059]/15" />
                <div>
                  <p className="text-xs text-[#C5A059]/60 uppercase tracking-wider">Enhancement</p>
                  <p className="text-base text-[#F4F0EA]/70 mt-1">None</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
